import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ChevronLeftIcon } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CYCLES } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

import useAppraisalRole, { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const emptyCycle = {
    name: "",
    description: "",
    period_start: null,
    period_end: null,
    self_eval_start: null,
    self_eval_end: null,
    rm_review_deadline: null,
    hr_review_deadline: null,
    goal_setting_start: null,
    goal_setting_end: null,
    status: "draft",
};

const AppraisalCycleAddEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { fetch, create, update } = useApiMethods();
    const { isHR } = useAppraisalRole();
    const { canAddCycle, canEditCycle } = useAppraisalPermissions();

    const allowed = isEdit ? canEditCycle : canAddCycle;

    const [form, setForm] = useState(emptyCycle);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        setLoading(true);
        fetch(APPRAISAL_CYCLES, { id })
            .then((res) => { if (!cancelled && res?.data) setForm(res.data); })
            .catch(() => { if (!cancelled) ToastMsg("error", "Failed to load cycle"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    // Bands are edited as one line -- "4.5 Outstanding, 3.5 Exceeds" -- because
    // a repeating min/label row builder is a lot of UI for something HR sets
    // once per cycle, if ever.
    const bandsToText = (bands) =>
        Array.isArray(bands) ? bands.map((b) => `${b.min} ${b.label}`).join(", ") : "";

    // Declared above the permission guard below: hooks must run in the same
    // order on every render, and an early return after them breaks that.
    const [bandText, setBandText] = useState("");
    useEffect(() => { setBandText(bandsToText(form.rating_bands)); }, [form.rating_bands]);

    if (!isHR && !allowed) {
        return (
            <ContainerCard scrollable>
                <div className="text-center py-12 text-muted-foreground">
                    Only HR can create or edit appraisal cycles.
                </div>
            </ContainerCard>
        );
    }

    const labelClass = "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1";

    const textToBands = (text) => {
        const parts = String(text || "").split(",").map((p) => p.trim()).filter(Boolean);
        const out = [];
        for (const part of parts) {
            const match = part.match(/^(\d+(?:\.\d+)?)\s+(.+)$/);
            // A line that does not parse is skipped rather than saved half-formed;
            // the API falls back to the default bands when the list is empty.
            if (match) out.push({ min: Number(match[1]), label: match[2].trim() });
        }
        return out.length ? out.sort((a, b) => b.min - a.min) : null;
    };

    // Locked once the cycle leaves draft: an active cycle may already have
    // graded appraisals, and re-banding them retroactively would change results
    // nobody re-reviewed.
    const isDraft = (form.status || "draft") === "draft";

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name?.trim()) e.name = "Name is required.";
        if (!form.period_start) e.period_start = "Period start is required.";
        if (!form.period_end) e.period_end = "Period end is required.";
        if (form.period_start && form.period_end && dayjs(form.period_end).isBefore(dayjs(form.period_start))) {
            e.period_end = "Period end must be after start.";
        }
        if (!form.self_eval_start) e.self_eval_start = "Self-eval start is required.";
        if (!form.self_eval_end) e.self_eval_end = "Self-eval end is required.";
        // Both or neither. One date alone leaves the cycle on the old flow,
        // which looks to HR like the goal phase silently did nothing.
        if (!!form.goal_setting_start !== !!form.goal_setting_end) {
            const missing = form.goal_setting_start ? "goal_setting_end" : "goal_setting_start";
            e[missing] = "Set both goal-setting dates, or leave both blank.";
        }
        if (form.goal_setting_start && form.goal_setting_end
            && dayjs(form.goal_setting_end).isBefore(dayjs(form.goal_setting_start))) {
            e.goal_setting_end = "Goal setting must end on or after it starts.";
        }
        if (form.goal_setting_end && form.self_eval_start
            && dayjs(form.goal_setting_end).isAfter(dayjs(form.self_eval_start))) {
            e.goal_setting_end = "Goal setting must close before self-evaluation opens.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            ToastMsg("error", "Please fix the highlighted fields");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                // Blank means "use the module defaults", so send null rather
                // than 0 or "" -- the API treats a falsy value as unset.
                rating_scale_max: form.rating_scale_max ? Number(form.rating_scale_max) : null,
                rating_bands: isDraft ? textToBands(bandText) : form.rating_bands,
                goal_setting_start: form.goal_setting_start || null,
                goal_setting_end: form.goal_setting_end || null,
                min_reviewers: form.min_reviewers ? Number(form.min_reviewers) : null,
                max_reviewers: form.max_reviewers ? Number(form.max_reviewers) : null,
                feedback_enabled: !!form.feedback_enabled,
                competency_enabled: !!form.competency_enabled,
            };
            const res = isEdit
                ? await update(APPRAISAL_CYCLES, { ...payload, id: Number(id) })
                : await create(APPRAISAL_CYCLES, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || `Cycle ${isEdit ? "updated" : "created"}`);
                navigate("/appraisal/cycles");
            } else {
                ToastMsg("error", res?.message || "Failed to save cycle");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save cycle");
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate("/appraisal/cycles");
    }

    return (
        <ContainerCard scrollable>
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={handleBack} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {isEdit ? "Edit Appraisal Cycle" : "Add Appraisal Cycle"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Define the period, self-evaluation window, and review deadlines
                    </p>
                </div>
            </div>

            <div className="px-3 pb-6">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 space-y-5 max-w-full">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Cycle Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="e.g. Q3 2026 Review"
                                    value={form.name || ""}
                                    onChange={(e) => setField("name", e.target.value)}
                                />
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <Textarea
                                    rows={2}
                                    placeholder="Optional notes about this cycle..."
                                    value={form.description || ""}
                                    onChange={(e) => setField("description", e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Period Start <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.period_start ? dayjs(form.period_start) : null}
                                    onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("period_start", iso || null) }}
                                    format="DD-MM-YYYY"
                                    className="w-full h-10"
                                />
                                {errors.period_start && <p className="text-xs text-red-500 mt-1">{errors.period_start}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Period End <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.period_end ? dayjs(form.period_end) : null}
                                    onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("period_end", iso || null) }}
                                    format="DD-MM-YYYY"
                                    className="w-full h-10"
                                />
                                {errors.period_end && <p className="text-xs text-red-500 mt-1">{errors.period_end}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Self-Eval Start <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.self_eval_start ? dayjs(form.self_eval_start) : null}
                                    onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("self_eval_start", iso || null) }}
                                    format="DD-MM-YYYY"
                                    className="w-full h-10"
                                />
                                {errors.self_eval_start && <p className="text-xs text-red-500 mt-1">{errors.self_eval_start}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Self-Eval End <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.self_eval_end ? dayjs(form.self_eval_end) : null}
                                    onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("self_eval_end", iso || null) }}
                                    format="DD-MM-YYYY"
                                    className="w-full h-10"
                                />
                                {errors.self_eval_end && <p className="text-xs text-red-500 mt-1">{errors.self_eval_end}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">RM Review Deadline</label>
                                <DatePicker
                                    value={form.rm_review_deadline ? dayjs(form.rm_review_deadline) : null}
                                    onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("rm_review_deadline", iso || null) }}
                                    format="DD-MM-YYYY"
                                    className="w-full h-10"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">HR Review Deadline</label>
                                <DatePicker
                                    value={form.hr_review_deadline ? dayjs(form.hr_review_deadline) : null}
                                    onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("hr_review_deadline", iso || null) }}
                                    format="DD-MM-YYYY"
                                    className="w-full h-10"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                <Select
                                    value={form.status}
                                    onChange={(v) => setField("status", v)}
                                    options={[
                                        { value: "draft", label: "Draft" },
                                        { value: "active", label: "Active" },
                                        { value: "closed", label: "Closed" },
                                    ]}
                                    className="w-full"
                                    size="large"
                                />
                            </div>
                        </div>

                        {/* Goal setting. Optional, and the whole phase hinges on
                            these two dates: with them, employees agree their goals
                            before the cycle runs; without them the cycle behaves
                            exactly as cycles did before this existed. */}
                        <div className="border-t border-border pt-4 mt-2">
                            <h3 className="font-semibold text-sm mb-1">Goal-setting window</h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                Optional. Set a window and employees must agree their goals
                                with their manager before self-evaluation opens &mdash; they can
                                also send them back for changes. Leave blank and goals go
                                straight to the employee as they do today.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Goal setting opens</label>
                                    <DatePicker
                                        value={form.goal_setting_start ? dayjs(form.goal_setting_start) : null}
                                        onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("goal_setting_start", iso || null) }}
                                        format="DD-MM-YYYY"
                                        className="w-full h-10"
                                    />
                                    {errors.goal_setting_start && (
                                        <p className="text-xs text-red-500 mt-1">{errors.goal_setting_start}</p>
                                    )}
                                </div>
                                <div>
                                    <label className={labelClass}>Goal setting closes</label>
                                    <DatePicker
                                        value={form.goal_setting_end ? dayjs(form.goal_setting_end) : null}
                                        onChange={(d) => { const iso = d ? d.format("YYYY-MM-DD") : ""; setField("goal_setting_end", iso || null) }}
                                        format="DD-MM-YYYY"
                                        className="w-full h-10"
                                    />
                                    {errors.goal_setting_end && (
                                        <p className="text-xs text-red-500 mt-1">{errors.goal_setting_end}</p>
                                    )}
                                </div>
                            </div>
                            {form.goal_setting_start && form.goal_setting_end && (
                                <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                                    Appraisals in this cycle will start at &ldquo;Pending Goal Agreement&rdquo;.
                                    Self-evaluation opens automatically on{" "}
                                    {form.self_eval_start
                                        ? dayjs(form.self_eval_start).format("DD-MM-YYYY")
                                        : "the self-evaluation start date"} once goals are agreed.
                                </p>
                            )}
                        </div>

                        {/* Rating scale. Stored on the cycle and frozen there:
                            changing the bands later must never re-grade
                            appraisals already completed under the old ones, so
                            these are editable only while the cycle is a draft. */}
                        <div className="border-t border-border pt-4 mt-2">
                            <h3 className="font-semibold text-sm mb-1">Rating scale</h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                Leave blank to use the standard 1-5 scale. Locked once the
                                cycle leaves draft, so completed appraisals keep the scale
                                they were graded on.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Highest rating</label>
                                    <Input
                                        type="number"
                                        min={2}
                                        max={10}
                                        placeholder="5"
                                        disabled={!isDraft}
                                        value={form.rating_scale_max ?? ""}
                                        onChange={(e) => setField("rating_scale_max", e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Band labels</label>
                                    <Input
                                        placeholder="4.5 Outstanding, 3.5 Exceeds, 2.5 Meets, 1.5 Needs Improvement, 0 Poor"
                                        disabled={!isDraft}
                                        value={bandText}
                                        onChange={(e) => setBandText(e.target.value)}
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        One band per comma: the minimum score, then its label.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Competency assessment, opt-in per cycle. Which framework
                            applies is resolved per employee from their designation
                            when the appraisal is assigned, so one cycle can span
                            several job families. */}
                        <div className="border-t border-border pt-4 mt-2">
                            <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-semibold text-sm">Competency assessment</h3>
                                <button
                                    type="button"
                                    onClick={() => setField("competency_enabled", !form.competency_enabled)}
                                    className={cn(
                                        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 cursor-pointer",
                                        form.competency_enabled ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"
                                    )}
                                >
                                    <span className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        form.competency_enabled ? "translate-x-4" : "translate-x-1"
                                    )} />
                                </button>
                                <span className="text-sm text-muted-foreground">
                                    {form.competency_enabled ? "On" : "Off"}
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                                Assesses how the work gets done alongside the KPIs. Competency
                                scores are reported separately and are never added into the
                                final appraisal score.
                            </p>
                        </div>

                        {/* 360 feedback is opt-in per cycle: a quarterly check-in
                            rarely wants a peer panel, an annual review usually does. */}
                        <div className="border-t border-border pt-4 mt-2">
                            <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-semibold text-sm">360&deg; feedback</h3>
                                <button
                                    type="button"
                                    onClick={() => setField("feedback_enabled", !form.feedback_enabled)}
                                    className={cn(
                                        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 cursor-pointer",
                                        form.feedback_enabled ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"
                                    )}
                                >
                                    <span className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        form.feedback_enabled ? "translate-x-4" : "translate-x-1"
                                    )} />
                                </button>
                                <span className="text-sm text-muted-foreground">
                                    {form.feedback_enabled ? "On" : "Off"}
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                                Employees nominate reviewers; their manager approves the panel.
                                Peer ratings are not counted towards the final score.
                            </p>
                            {form.feedback_enabled && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Minimum reviewers</label>
                                        <Input type="number" min={1} placeholder="2"
                                            value={form.min_reviewers ?? ""}
                                            onChange={(e) => setField("min_reviewers", e.target.value)} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Maximum reviewers</label>
                                        <Input type="number" min={1} placeholder="8"
                                            value={form.max_reviewers ?? ""}
                                            onChange={(e) => setField("max_reviewers", e.target.value)} />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                            <Button variant="outline" onClick={() => navigate("/appraisal/cycles")}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                variant="brand"
                            >
                                <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : (isEdit ? "Update Cycle" : "Create Cycle")}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default AppraisalCycleAddEdit;
